import { useFormik } from "formik";
import React from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { addReferralOffer } from "../../apis/referral-offers/add-referral-offers";
import { getTodayDate } from '../promoCode/AddPromoCodeModal';
import Button from "../Button";
import DatePicker from '../DatePicker';
import Input from "../Input";
import Modal from "../Modal";
import Select from "../Select";

interface AddOfferModalProps {
  isVisible: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

type Offer = {
  credits: number | string;
  pointsRequired: number | string;
  discountValue: number | string;
  discountType: string;
  serviceType: string;
  isOfferActive: boolean;
  userType: string;
  expiryDate: string;
  startDate: string;
};

const AddOfferModal = ({
  isVisible,
  onHide,
  onSuccess,
}: AddOfferModalProps) => {
  const formik = useFormik<Offer>({
    initialValues: {
      credits: "",
      pointsRequired: "",
      discountValue: "",
      discountType: "",
      serviceType: "",
      expiryDate: "",
      startDate: "",
      isOfferActive: false,
      userType: "",
    },
    validationSchema: Yup.object({
      credits: Yup.number()
        .required("Credits are required")
        .integer("Decimal values are not allowed"),
      isOfferActive: Yup.boolean().required("Offer Status is required"),
      startDate: Yup.date()
        .required("This field is required")
        .min(getTodayDate(), "Start Date must be today or a future date")
        .test(
          "is-future-5-min",
          "Start time must be future from now",
          function (value) {
            if (!value) return true;
            const now = new Date();
            const selected = new Date(value);
            return selected.getTime() >= now.getTime() - 1 * 60 * 1000;
          }
        ),
      userType: Yup.string().required("This field is required"),
      expiryDate: Yup.string()
        .nullable()
        .test(
          "is-same-or-after-date",
          "Expiry date must be the same day or after the start date",
          function (value) {
            const { startDate } = this.parent;
            if (!value || !startDate) return true;
            const start = new Date(startDate);
            const expiry = new Date(value);
            return expiry.setHours(0, 0, 0, 0) >= start.setHours(0, 0, 0, 0);
          }
        )
        .test(
          "is-5-minutes-later",
          "Expiry time must be at least 5 minutes after the start time",
          function (value) {
            const { startDate } = this.parent;
            if (!value || !startDate) return true;

            const expiry = new Date(value).getTime();
            const start = new Date(startDate).getTime();

            return expiry >= start + 5 * 60 * 1000;
          }
        ),
    }),

    onSubmit: async (values) => {
      const payload = {
        credits: values.credits,
        isOfferActive: values.isOfferActive,
        startDate: values.startDate,
        expiryDate: values.expiryDate,
        userType: values.userType,
      };

      const res = await addReferralOffer(payload);
      if (res.success) {
        toast.success(res.data.message || "Offer added successfully");
        formik.resetForm();
        onSuccess();
        onHide();
      } else {
        toast.error(res.error.error || "Failed to add offer");
      }
    },
  });
  return (
    <Modal
      heading="Add offer"
      isVisible={isVisible}
      onHide={() => {
        formik.resetForm();
        onHide();
      }}
    >
      <form onSubmit={formik.handleSubmit} className='px-8'>
        <div className="md:grid md:col-span-5 md:grid-cols-2 md:gap-x-4 flex flex-col gap-y-2">
          <Input
            placeholder="$100"
            className="input-primary text-[1rem] lg:text-xs"
            label="Credits *"
            name="credits"
            type="number"
            value={formik.values.credits}
            onChange={formik.handleChange}
            error={formik.errors.credits}
            onBlur={formik.handleBlur}
            isInvalid={Boolean(formik.touched.credits && formik.errors.credits)}
          />
          <Select
            placeholder="Select status"
            className="select-primary text-[1rem] lg:text-xs"
            label="Offer activation *"
            name="isOfferActive"
            value={formik.values.isOfferActive ? "yes" : "no"}
            onChange={(e) =>
              formik.setFieldValue("isOfferActive", e.target.value === "yes")
            }
            options={[
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
            ]}
          />
          <DatePicker
            name="startDate"
            label="Valid from *"
            placeholderText='MM/DD/YYYY'
            className='text-[1rem] lg:text-xs'
            inputClassName='text-[1rem] lg:text-xs'
            selected={
              formik.values.startDate ? new Date(formik.values.startDate) : null
            }
            onChange={(date) => {
              formik.setFieldValue(
                "startDate",
                date ? date.toISOString() : null
              );
            }}
            onBlur={formik.handleBlur}
            error={formik.errors.startDate}
            isInvalid={Boolean(
              formik.touched.startDate && formik.errors.startDate
            )}
            dateFormat="MM/dd/yyyy HH:mm"
            showTimeSelect
            popperPlacement="top"
            timeFormat="HH:mm"
            timeIntervals={5}
            wrapperClassName='w-full'
          />

          <DatePicker
            name="expiryDate"
            className='text-[1rem] lg:text-xs'
            inputClassName='text-[1rem] lg:text-xs'
            label="Expiry date (optional)"
            placeholderText='MM/DD/YYYY'
            selected={
              formik.values.expiryDate
                ? new Date(formik.values.expiryDate)
                : null
            }
            onChange={(date: Date | null) =>
              formik.setFieldValue(
                "expiryDate",
                date ? date.toISOString() : null
              )
            }
            onBlur={formik.handleBlur}
            error={formik.errors.expiryDate}
            isInvalid={Boolean(
              formik.touched.expiryDate && formik.errors.expiryDate
            )}
            dateFormat="MM/dd/yyyy HH:mm"
            showTimeSelect
            popperPlacement="top"
            timeFormat="HH:mm"
            timeIntervals={5}
          />

          <Select
            placeholder="Select type"
            className="select-primary text-[1rem] lg:text-xs"
            label="User type *"
            name="userType"
            value={formik.values.userType}
            onChange={(e) => formik.setFieldValue("userType", e.target.value)}
            options={[
              { value: "driver", label: "Driver" },
              { value: "individual", label: "Individual user" },
              { value: "business", label: "Business user" },
            ]}
            isInvalid={Boolean(
              formik.touched.userType && formik.errors.userType
            )}
            onBlur={formik.handleBlur}
            error={formik.errors.userType}
          />
        </div>
        <div className="flex mt-6 gap-2 items-center justify-center">
          <Button
            className="w-full md:w-32 min-h-0 h-10 md:h-10"
            isLoading={formik.isSubmitting}
            type="submit"
          >
            Add
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddOfferModal;
